import { z } from "zod";

import {
	MAX_COMPETENCY_LEVELS,
	MAX_ROLE_DESCRIPTION_LENGTH,
	MAX_ROLE_NAME_LENGTH,
} from "@performyard/service-contracts";
import type { CreateRoleBody } from "@performyard/service-contracts";

import { stripHtmlTags } from "../../../../utils/stripHtmlTags";

export const ROLES_LIST_PATH = "/administration/roles";

const trimmedNameField = z
	.string()
	.trim()
	.pipe(
		z
			.string()
			.min(1, "Role name is required")
			.max(MAX_ROLE_NAME_LENGTH, `Role name must be ${MAX_ROLE_NAME_LENGTH} characters or less`)
	);

const descriptionField = z
	.string()
	.default("")
	.refine((val) => stripHtmlTags(val).length <= MAX_ROLE_DESCRIPTION_LENGTH, {
		message: `Description must be ${MAX_ROLE_DESCRIPTION_LENGTH} characters or less`,
	});

const competencyEntrySchema = z.object({
	competency_id: z.string().min(1),
	expected_level: z.number().int().min(1).max(MAX_COMPETENCY_LEVELS).nullable().default(null),
});

export const roleFormSchema = z.object({
	name: trimmedNameField,
	description: descriptionField,
	competencies: z.array(competencyEntrySchema).default([]),
});

export type RoleFormValues = z.infer<typeof roleFormSchema>;
export type RoleCompetencyEntry = z.infer<typeof competencyEntrySchema>;

export const EMPTY_FORM_VALUES: RoleFormValues = {
	name: "",
	description: "",
	competencies: [],
};

interface RoleForForm {
	name: string;
	description: string | null | undefined;
	competencies: Array<{ competency_id: string; expected_level: number | null }>;
}

export const fromRole = (role: RoleForForm): RoleFormValues => ({
	name: role.name,
	description: role.description ?? "",
	competencies: role.competencies.map((entry) => ({
		competency_id: entry.competency_id,
		expected_level: entry.expected_level,
	})),
});

// The CreateRoleBody type uses `z.infer<>` so its competency_id is the post-transform ObjectId. We send
// strings over JSON and the backend Zod transforms them — bridge the type-level gap with `unknown`.
// When the competency FF is off the editor is hidden; omit competencies entirely so a base
// name/description edit doesn't resend a stored array (which the FF-off backend guard rejects).
export const toRequestPayload = (values: RoleFormValues, includeCompetencies = true): CreateRoleBody => {
	const base = { name: values.name, description: values.description };
	if (!includeCompetencies) {
		return base as unknown as CreateRoleBody;
	}
	return {
		...base,
		competencies: values.competencies.map((entry) => ({
			competency_id: entry.competency_id,
			expected_level: entry.expected_level,
		})),
	} as unknown as CreateRoleBody;
};
